/**
 * Auto-zoom on emphasized words — Submagic-style polish.
 *
 * What it does:
 *   When the prosody analyser tags a word as emphasis === 'high', we briefly
 *   "punch in" on the video around that word's time window. The zoom is a
 *   short scale+crop pulse (1.00 → peak → 1.00), centred on the frame.
 *   No external dependencies — relies on FFmpeg's zoompan filter that's
 *   already in libavfilter.
 *
 * Why this isn't "auto-zoom on every word":
 *   1. emphasis === 'high' is the top ~12% of words by prosody score, so
 *      it's already sparse.
 *   2. We additionally debounce — at most one zoom per 1.5 s window —
 *      so a phrase like "BIG! BIG! BIG!" doesn't strobe.
 *
 * Filter shape:
 *   We use FFmpeg's `zoompan` with a piecewise zoom expression that picks
 *   the peak zoom inside each emphasis window and 1.0 elsewhere. The expression
 *   is bigger than a single between() — we chain `if(between(t,S0,E0),Z,…)`
 *   for each event, falling through to 1.0. This is one filter pass, not N
 *   filters, which keeps the graph tractable for libavfilter.
 *
 * Math note:
 *   `zoompan` operates on input frames and emits output frames at `fps`.
 *   When zoom == 1.0 we want pass-through; we still need x/y centring so
 *   `iw/zoom/2` and `ih/zoom/2` reduce to `iw/2 - iw/2 = 0` and pad to
 *   the centre of the source frame — i.e. no visual change.
 *
 * Public API:
 *   window.__acAutoZoom = {
 *     isEnabled(),
 *     getIntensity(),
 *     setEnabled(on),
 *     setIntensity(level),
 *     buildFilterArgs(words, videoWidth, videoHeight, fps)
 *   }
 */
(function(){
    'use strict';

    /* Single source of truth for state. Settings restored via
       __toolSettings.bind() in bindControls(). Defaults: OFF, punchy. */
    var state = {
        enabled: false,
        intensity: 'punchy',
    };

    /* Peak zoom factor per intensity. Picked to feel "noticeable but not
       jarring" at 1080p:
         subtle    — barely above 1, almost a breath
         punchy    — Submagic-style obvious pop
         cinematic — heavy push-in, ~10% of frame disappears
       Keep all under 1.12 — beyond that, libass-rendered captions get
       visibly soft (zoompan re-samples). */
    var ZOOM_PEAK = {
        subtle:    1.04,
        punchy:    1.08,
        cinematic: 1.12,
    };

    /* Minimum gap between zoom events (seconds). Prevents strobing on
       consecutive high-emphasis words. */
    var DEBOUNCE_S = 1.5;

    /* Per-event zoom window. We slightly pad the word boundaries so the
       pulse covers the leading consonant + trailing vowel — feels more
       natural than snapping exactly to the word's t0/t1. */
    var PRE_PAD_S  = 0.08;
    var POST_PAD_S = 0.12;

    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

    /* Deduplicate emphasis events: keep the first high-emphasis word in
       any DEBOUNCE_S window. Returns an array of {start, end} segments
       with optional padding already applied. */
    function collectEvents(words) {
        var out = [];
        if (!Array.isArray(words) || !words.length) return out;
        var lastStart = -Infinity;
        for (var i = 0; i < words.length; i++) {
            var w = words[i];
            if (!w || w.emphasis !== 'high') continue;
            var s = +w.start;
            var e = +w.end;
            if (!isFinite(s) || !isFinite(e) || e <= s) continue;
            if (s - lastStart < DEBOUNCE_S) continue;
            out.push({
                start: Math.max(0, s - PRE_PAD_S),
                end:   e + POST_PAD_S,
            });
            lastStart = s;
        }
        return out;
    }

    /* Sanity check: every '(' has a matching ')'. Returns true if balanced.
       Used by buildFilterArgs to refuse to emit broken filter strings. */
    function balancedParens(str) {
        var depth = 0;
        for (var i = 0; i < str.length; i++) {
            var c = str.charCodeAt(i);
            if (c === 40) depth++;       // '('
            else if (c === 41) {          // ')'
                depth--;
                if (depth < 0) return false;
            }
        }
        return depth === 0;
    }

    /* Construct the piecewise zoom expression:
         if(between(t,S0,E0), Z*easing0,
         if(between(t,S1,E1), Z*easing1,
         ...
         1))) */
    function buildZoomExpr(events, peak) {
        if (!events.length) return '1';
        var head = '';
        var tail = '';
        for (var i = 0; i < events.length; i++) {
            var s = events[i].start.toFixed(3);
            var e = events[i].end.toFixed(3);
            var dur = (events[i].end - events[i].start).toFixed(3);
            /* Triangle easing: 1.0 at endpoints, peak at midpoint.
                 mid = (S+E)/2
                 rampUp   = (t-S)/(mid-S)  on [S, mid]
                 rampDown = (E-t)/(E-mid)  on [mid, E]
                 envelope = min(rampUp, rampDown)  → triangle wave 0..1
                 z = 1 + (peak-1) * envelope
               One-liner using min() because libavfilter supports it. */
            var mid = ((events[i].start + events[i].end) / 2).toFixed(3);
            // Guard divisor: if dur ~ 0 (shouldn't happen post-dedupe) fall to peak.
            // We use max() to floor the divisor at 0.001 so libavfilter never sees /0.
            var envelope = "min((t-" + s + ")/max(" + mid + "-" + s + "\\,0.001)\\,(" + e + "-t)/max(" + e + "-" + mid + "\\,0.001))";
            var zexpr = "(1+(" + peak.toFixed(3) + "-1)*" + envelope + ")";
            head += "if(between(t\\," + s + "\\," + e + ")\\," + zexpr + "\\,";
            tail += ")";
        }
        return head + "1" + tail;
    }

    /* Sanitise unwrapped expression for inclusion as `-vf` arg value.
       FFmpeg filter args parse ':' as kv separator, ',' as filter separator,
       and we use '\\,' to escape commas inside expressions. The whole filter
       string still goes into a single argv slot, so shell quoting is the
       caller's problem. We just ensure no raw ':' or ',' leak. */
    function sanitiseExpr(expr) {
        // We already use \, for commas. ':' shouldn't appear in our expressions.
        if (expr.indexOf(':') !== -1) {
            // Strip — shouldn't happen but defensive.
            expr = expr.split(':').join('');
        }
        return expr;
    }

    /**
     * Build the FFmpeg filter string for the current state + word list.
     *
     * Returns '' (empty) when:
     *   – feature disabled
     *   – no high-emphasis words
     *   – no video dimensions provided
     *
     * Returns a `zoompan=…` filter string otherwise. Caller appends to its
     * existing filter chain (typically before `ass=…` so the subtitle text
     * sits on top of the zoomed frame).
     */
    function buildFilterArgs(words, videoWidth, videoHeight, fps) {
        if (!state.enabled) return '';
        if (!Array.isArray(words) || !words.length) return '';

        var w = parseInt(videoWidth, 10) || 0;
        var h = parseInt(videoHeight, 10) || 0;
        if (w <= 0 || h <= 0) return '';

        var f = parseInt(fps, 10);
        if (!isFinite(f) || f <= 0) f = 30;
        f = clamp(f, 1, 120);

        var events = collectEvents(words);
        if (!events.length) return '';

        var peak = ZOOM_PEAK[state.intensity] || ZOOM_PEAK.punchy;
        var zoomExpr = sanitiseExpr(buildZoomExpr(events, peak));

        // Center the crop rectangle. zoompan uses iw/zoom and ih/zoom as the
        // output crop size, so centring means (iw - iw/zoom)/2 on x, same on y.
        var xExpr = "iw/2-(iw/zoom/2)";
        var yExpr = "ih/2-(ih/zoom/2)";

        // d=1 means each input frame emits one output frame — pass-through
        // count, not "duration in frames at fps". We pin s=WxH so zoompan
        // doesn't try to scale to its default 1280x720.
        // fps mirrors the input fps so we don't accidentally retime.
        var filter = 'zoompan=' +
            "z='" + zoomExpr + "'" +
            ":x='" + xExpr + "'" +
            ":y='" + yExpr + "'" +
            ":d=1" +
            ":fps=" + f +
            ":s=" + w + 'x' + h;

        // Final sanity: balanced parens or refuse to emit.
        if (!balancedParens(filter)) {
            try { console.warn('[auto-zoom] filter has unbalanced parens, skipping', filter); } catch(e){}
            return '';
        }
        return filter;
    }

    window.__acAutoZoom = {
        isEnabled: function() { return !!state.enabled; },
        getIntensity: function() { return state.intensity; },

        setEnabled: function(on) {
            state.enabled = !!on;
            try {
                if (window.__toolSettings) window.__toolSettings.save('auto-captions', 'autoZoomOn', state.enabled);
            } catch(e){}
        },
        setIntensity: function(level) {
            if (!ZOOM_PEAK[level]) return;
            state.intensity = level;
            try {
                if (window.__toolSettings) window.__toolSettings.save('auto-captions', 'autoZoomIntensity', level);
            } catch(e){}
        },

        buildFilterArgs: buildFilterArgs,

        /* Internals exposed for testing — do not depend on these in production code. */
        _collectEvents: collectEvents,
        _ZOOM_PEAK: ZOOM_PEAK,
        _DEBOUNCE_S: DEBOUNCE_S,
    };
})();
